import { times, groupBy, map } from 'lodash'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { trackInternalEvent } from '@rio/tracking'

import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import ApplyModal from 'builder/components/ApplyModal'
import LimitPaywallModal from 'builder/components/LimitPaywallModal'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { Icon24 } from 'builder/components/Icon'
import { useI18n } from 'builder/hooks/useI18n'
import { selectors as careerProfileSelectors } from 'builder/modules/careerProfile'

import { selectors as jobSearchSelectors } from 'builder/modules/jobSearch'
import JobDetails, { JobDetailsSkeleton } from '../JobDetails'
import JobItem, { JobItemSkeleton } from '../JobItem'

import { NoResultsSearch } from '../Banners/NoResultsSearch/NoResultsSearch'
import { useJobSearch } from '../useJobSearch'
import { TabType } from '../types'
import { NoResultsRecommendation } from '../Banners/NoResultsSearch/NoResultsRecommendation'
import JobGroup from '../JobGroup'
import { ManagerJobAlertButton } from '../AlertModal/ManageJobAlertButton/ManageJobAlertButton'
import { PageActionButtons } from '../PageActionButtons/PageActionButtons'
import { JobFilters } from '../JobFilters'
import { NoLocation, SetTargetLocationDialog } from '../RecommendationsMissingInfoComponents'
import { ConfirmModal } from '../AutoApply/TailorResume/ConfirmModal'
import { ResumeSelectModal } from '../AutoApply/TailorResume/ResumeSelectModal'
import {
  Details,
  DetailsFixedContainer,
  Grid,
  JobsList,
  SimilarJobsTitle,
  RecommendedJobsDescription,
  RecommendedJobsDescriptionContainer,
  SearchForInsteadLink,
  SearchResultsSubtitle,
  SearchResultsTitle,
  HeadingContainer,
  HeadingContainerJobAlertButton,
  MissingInfoWrapper,
} from './styles'
import { ClusteredJob, Job, ResultStats } from './types'

interface Props {
  results: Job[]
  isSearching: boolean
  isLoading: boolean
  isNoResults: boolean
  activeJob: Job | null
  stats: ResultStats | null
  similarJobsStats?: ResultStats | null
  similarResults?: Job[]
  correctedTerm?: string | null
  searchTerm?: string
  setActiveJob: (job: Job | null) => void
  loadNextPage: () => void
  loadNextSimilarJobsPage?: () => void
}

export const JobSearchResult = ({
  results,
  isSearching,
  isLoading,
  isNoResults,
  stats,
  activeJob,
  setActiveJob,
  loadNextPage,
  similarResults,
  similarJobsStats,
  loadNextSimilarJobsPage = () => {},
  correctedTerm,
  searchTerm,
}: Props) => {
  const {
    selectedTabId,
    handleChangeSelectedTabId,
    updateSearchParams,
    performRecommendations,
    filters,
    externalSlugId,
  } = useJobSearch()
  const { i18n } = useI18n()
  const enableJobAlertFeature = useTypedSelector(jobSearchSelectors.enableJobAlertFeature)

  const { features } = useFeaturesConfig()

  const { isPhone } = useMediaQueries()

  const [limitPaywallOpen, setLimitPaywallOpen] = useState<boolean>(false)
  const [applyDialog, setApplyDialog] = useState<boolean>(false)
  const [displaySetTargetLocationDialog, setDisplaySetTargetLocationDialog] =
    useState<boolean>(false)

  const ref = useRef<HTMLDivElement | null>(null)

  const profileTargetLocations = useTypedSelector(careerProfileSelectors.profileTargetLocations)
  const profileTargetRoles = useTypedSelector(careerProfileSelectors.profileTargetRoles)
  // Infinite scroll logic
  const hasNextPage = results.length < (stats?.total_jobs || 0)
  const hasNextPageSimilarJobs = similarResults?.length
    ? similarResults.length < (similarJobsStats?.total_jobs || 0)
    : false

  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasNextPage || hasNextPageSimilarJobs,
    onLoadMore: hasNextPage ? loadNextPage : loadNextSimilarJobsPage,
  })

  const scrollToJob = () => {
    const job = document.getElementById(`card${externalSlugId}`)
    if (job) {
      job.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const clusteredJobs = useMemo((): ClusteredJob[] => {
    const resultWithoutGroup = results.find(el => el.group === null || el.group === undefined)
    if (resultWithoutGroup || !stats?.page_size) {
      return results.map(el => ({
        ...el,
        relatedJobs: [],
      }))
    }

    const grouped = groupBy(
      results.map((el, index) => ({
        ...el,
        group: Math.floor(index / stats.page_size) * stats.page_size + (el.group as number),
      })),
      'group',
    )

    const clustered = map(grouped, jobGroup => {
      const [parent, ...relatedJobs] = jobGroup

      return {
        ...parent,
        relatedJobs,
      }
    })
    return clustered
  }, [results, stats])

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = 0
    }
  }, [activeJob])

  useEffect(() => {
    if (!isLoading && externalSlugId) {
      scrollToJob()
    }
  }, [externalSlugId, isLoading])

  const handleSelectJob = (job: Job) => {
    setActiveJob(job)
  }

  const renderProfileText = () => {
    if (selectedTabId === TabType.recommendation && !isNoResults) {
      return (
        <RecommendedJobsDescriptionContainer>
          <RecommendedJobsDescription>
            <Icon24.DoubleStars style={{ marginRight: 4 }} />
            {i18n.t(`builder.job_search.recommendations.description.lead`)}{' '}
            <Link
              to="/career-profile/edit?section=jobPreferences&returnUrl=/job-search?view=recommendation"
              onClick={() => {
                trackInternalEvent('click_edit_preferences', { tab: 'job_preferences' })
                trackInternalEvent('view_profile_editor', { label: 'recommended_jobs' })
              }}
            >
              {i18n.t(`builder.job_search.recommendations.description.cta`)}
            </Link>
          </RecommendedJobsDescription>
        </RecommendedJobsDescriptionContainer>
      )
    }
    return null
  }

  const renderMissingProfileInfoComponent = () => {
    const hasTargetRole = !!profileTargetRoles?.length || stats?.target_role_from_resume
    const hasTargetLocation = !!profileTargetLocations?.length || stats?.location_from_resume

    const dontShow = isNoResults || selectedTabId !== TabType.recommendation

    if ((hasTargetRole && hasTargetLocation) || dontShow) return null

    return (
      <>
        {!hasTargetLocation && (
          <MissingInfoWrapper>
            <NoLocation onClick={() => setDisplaySetTargetLocationDialog(true)} />
          </MissingInfoWrapper>
        )}
        {displaySetTargetLocationDialog && (
          <SetTargetLocationDialog
            afterSetLocationHandler={() => {
              setTimeout(() => performRecommendations(filters, 1), 500)
            }}
            closeHandler={() => setDisplaySetTargetLocationDialog(false)}
          />
        )}
      </>
    )
  }

  const renderNoSearch = () => {
    if (isNoResults) {
      if (selectedTabId === TabType.recommendation) {
        return (
          <NoResultsRecommendation handleSearch={() => handleChangeSelectedTabId(TabType.search)} />
        )
      } else {
        return <NoResultsSearch />
      }
    }
    return null
  }

  const jobAlertButton = enableJobAlertFeature && (
    <HeadingContainerJobAlertButton>
      <ManagerJobAlertButton />
    </HeadingContainerJobAlertButton>
  )

  const disabledAutocorrect = true
  return (
    <>
      <HeadingContainer>
        {isPhone && <PageActionButtons />}

        {selectedTabId === TabType.recommendation && <JobFilters>{jobAlertButton}</JobFilters>}
        {selectedTabId === TabType.search && <JobFilters>{jobAlertButton}</JobFilters>}
      </HeadingContainer>

      {renderProfileText()}
      {renderNoSearch()}
      <Grid>
        <JobsList>
          {isSearching ? (
            <>
              {times(6).map(key => (
                <JobItemSkeleton key={key} />
              ))}
            </>
          ) : (
            <>
              {!disabledAutocorrect && !!correctedTerm && results.length > 0 && (
                <>
                  <SearchResultsTitle>
                    {i18n.t(`builder.job_search.job_search_results.show_results_for`)}{' '}
                    {correctedTerm}
                  </SearchResultsTitle>
                  <SearchResultsSubtitle>
                    {i18n.t(`builder.job_search.job_search_results.search_instead_for`)}{' '}
                    <SearchForInsteadLink
                      onClick={() => updateSearchParams({ correct_term: false })}
                    >
                      {searchTerm}
                    </SearchForInsteadLink>
                  </SearchResultsSubtitle>
                </>
              )}
              {renderMissingProfileInfoComponent()}
              {clusteredJobs.map((job, index) => (
                <Fragment key={job.id}>
                  <JobGroup
                    setJobAsActive={handleSelectJob}
                    parentJob={{ ...job, position: index + 1 }}
                    activeJobId={activeJob?.id}
                  />
                </Fragment>
              ))}
              {hasNextPage && <JobItemSkeleton />}
              {!hasNextPage && similarResults?.length ? (
                <>
                  <SimilarJobsTitle withMarginTop={results.length > 0}>
                    {i18n.t('builder.job_search.job_search_results.similar_jobs')}
                  </SimilarJobsTitle>
                  {similarResults.map((job, index) => (
                    <Fragment key={index}>
                      <JobItem
                        isActive={job.id === activeJob?.id}
                        job={{ ...job, position: index + 1 }}
                        onClick={() => handleSelectJob(job)}
                      />
                    </Fragment>
                  ))}
                  {hasNextPageSimilarJobs && <JobItemSkeleton />}
                </>
              ) : null}
            </>
          )}
          <li ref={infiniteRef} />
        </JobsList>
        {!isNoResults && (
          <Details $isSearching={isSearching} hasActiveJob={!!activeJob}>
            <DetailsFixedContainer
              ref={ref}
              $isSearching={isSearching}
              $isDashboardV2={!!features.superApp || !!features.international}
            >
              {isSearching && <JobDetailsSkeleton />}
              {activeJob && !isSearching && (
                <JobDetails
                  job={activeJob}
                  setApplyDialog={setApplyDialog}
                  goBack={() => setActiveJob(null)}
                  selectedTabId={selectedTabId}
                />
              )}
            </DetailsFixedContainer>
          </Details>
        )}

        {limitPaywallOpen && (
          <LimitPaywallModal
            limitPaywallOpen={limitPaywallOpen}
            setLimitPaywallOpen={setLimitPaywallOpen}
            from="search"
          />
        )}

        {applyDialog && activeJob && (
          <ApplyModal
            job={activeJob}
            applyDialog={applyDialog}
            setApplyDialog={setApplyDialog}
            setLimitPaywallOpen={setLimitPaywallOpen}
            parameterTracking={
              selectedTabId === TabType.recommendation ? 'recommendation' : 'search'
            }
          />
        )}
      </Grid>

      <ConfirmModal />
      <ResumeSelectModal />
    </>
  )
}
